import { useRef, useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { VAT_COUNTRY_OPTIONS, VAT_RATES } from '../../utils/vatRates';

const FORMULA_OPTIONS = [
    { label: '=const("value")',                   value: 'const' },
    { label: '=year(Field)',                       value: 'year' },
    { label: '=round(Field)',                      value: 'round' },
    { label: '=concat("sep", Field1, ..., FieldN)', value: 'concat' },
    { label: '=replace(Field, search, repl, ...)', value: 'replace' },
    { label: '=fakeVinIfEmpty(Field)',              value: 'fakevinifempty' },
    { label: '=fakeVin()',                          value: 'fakevin' },
    { label: '=addVat(Field, percent)',             value: 'addvat' },
    { label: '=substractVat(Field, percent)',       value: 'substractvat' },
];

function FormulaDialog({ visible, formulaType, previewColumns, onApply, onHide }) {
    const colOptions = previewColumns.map(c => ({ label: c, value: c }));

    // ── year / round (single field picker) ─────────────────────────────────────
    const [singleField, setSingleField] = useState('');

    // ── const ──────────────────────────────────────────────────────────────────
    const [constVal, setConstVal] = useState('');

    // ── concat ─────────────────────────────────────────────────────────────────
    const [concatSep, setConcatSep]       = useState(' ');
    const [concatFields, setConcatFields] = useState(['']);
    const addConcatField  = () => setConcatFields(f => [...f, '']);
    const setConcatField  = (i, v) => setConcatFields(f => f.map((x, j) => j === i ? v : x));
    const removeConcatField = (i) => setConcatFields(f => f.filter((_, j) => j !== i));

    // ── replace ────────────────────────────────────────────────────────────────
    const [replaceField, setReplaceField]  = useState('');
    const [replacePairs, setReplacePairs]  = useState([{ search: '', repl: '' }]);
    const addReplacePair    = () => setReplacePairs(p => [...p, { search: '', repl: '' }]);
    const setReplacePair    = (i, key, v) => setReplacePairs(p => p.map((x, j) => j === i ? { ...x, [key]: v } : x));
    const removeReplacePair = (i) => setReplacePairs(p => p.filter((_, j) => j !== i));

    // ── addVat / substractVat (field + country picker, percent resolved from country) ──────────
    const [vatField, setVatField]     = useState('');
    const [vatCountry, setVatCountry] = useState('');
    const vatPercent = vatCountry ? VAT_RATES[vatCountry].vatRate : null;

    const reset = () => {
        setConstVal('');
        setSingleField('');
        setConcatSep(' ');
        setConcatFields(['']);
        setReplaceField('');
        setReplacePairs([{ search: '', repl: '' }]);
        setVatField('');
        setVatCountry('');
    };

    const handleApply = () => {
        let formula = '';
        if (formulaType === 'year' || formulaType === 'round' || formulaType === 'fakevinifempty') {
            if (!singleField) return;
            formula = `=${formulaType}(${singleField})`;
        } else if (formulaType === 'const') {
            formula = `=const("${constVal}")`;
        } else if (formulaType === 'concat') {
            const fields = concatFields.filter(Boolean);
            if (!fields.length) return;
            formula = `=concat("${concatSep}", ${fields.join(', ')})`;
        } else if (formulaType === 'replace') {
            if (!replaceField) return;
            const pairs = replacePairs.flatMap(p => [`"${p.search}"`, `"${p.repl}"`]);
            formula = `=replace(${replaceField}, ${pairs.join(', ')})`;
        } else if (formulaType === 'addvat' || formulaType === 'substractvat') {
            if (!vatField || !vatCountry) return;
            const fnName = formulaType === 'addvat' ? 'addVat' : 'substractVat';
            formula = `=${fnName}(${vatField}, ${vatPercent})`;
        }
        if (formula) { onApply(formula); reset(); }
    };

    const titles = { const: 'Constant value', year: 'Year from date', round: 'Round to nearest 1000', concat: 'Concatenate fields', replace: 'Replace in field', fakevinifempty: 'Fake VIN if empty', fakevin: 'Fake VIN', addvat: 'Add VAT', substractvat: 'Subtract VAT' };

    const footer = (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button label="Cancel" text onClick={() => { onHide(); reset(); }} />
            <Button label="Apply" onClick={handleApply} />
        </div>
    );

    return (
        <Dialog header={titles[formulaType] ?? 'Formula'} visible={visible} onHide={() => { onHide(); reset(); }}
                footer={footer} style={{ minWidth: 380 }} modal>
            {(formulaType === 'year' || formulaType === 'round' || formulaType === 'fakevinifempty') && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <label style={{ fontWeight: 500 }}>Source field</label>
                    <Dropdown value={singleField} options={colOptions} onChange={e => setSingleField(e.value)}
                              placeholder="Select field" filter style={{ width: '100%' }} autoFocus />
                    {singleField && <span style={{ color: '#888', fontSize: '0.8rem' }}>{`=${formulaType}(${singleField})`}</span>}
                </div>
            )}

            {formulaType === 'const' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <label style={{ fontWeight: 500 }}>Constant value</label>
                    <InputText value={constVal} onChange={e => setConstVal(e.target.value)}
                               placeholder='e.g. FR' autoFocus />
                    <span style={{ color: '#888', fontSize: '0.8rem' }}>{`=const("${constVal}")`}</span>
                </div>
            )}

            {formulaType === 'concat' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <label style={{ fontWeight: 500 }}>Separator</label>
                    <InputText value={concatSep} onChange={e => setConcatSep(e.target.value)}
                               placeholder='e.g. " " or "-"' autoFocus />
                    <label style={{ fontWeight: 500 }}>Fields to concatenate</label>
                    {concatFields.map((f, i) => (
                        <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            <Dropdown value={f} options={colOptions} onChange={e => setConcatField(i, e.value)}
                                      placeholder="Select field" filter style={{ flex: 1 }} />
                            {concatFields.length > 1 &&
                                <Button icon="pi pi-times" text severity="danger" onClick={() => removeConcatField(i)} />}
                        </div>
                    ))}
                    <Button label="+ Add field" text onClick={addConcatField} style={{ alignSelf: 'flex-start' }} />
                    <span style={{ color: '#888', fontSize: '0.8rem' }}>
                        {`=concat("${concatSep}", ${concatFields.filter(Boolean).join(', ')})`}
                    </span>
                </div>
            )}

            {formulaType === 'replace' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div>
                        <label style={{ fontWeight: 500 }}>Source field</label>
                        <Dropdown value={replaceField} options={colOptions} onChange={e => setReplaceField(e.value)}
                                  placeholder="Select field" filter style={{ width: '100%', marginTop: 4 }} />
                    </div>
                    <label style={{ fontWeight: 500 }}>Search / Replace pairs</label>
                    {replacePairs.map((p, i) => (
                        <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            <InputText value={p.search} onChange={e => setReplacePair(i, 'search', e.target.value)}
                                       placeholder="Search" style={{ flex: 1 }} />
                            <span>→</span>
                            <InputText value={p.repl} onChange={e => setReplacePair(i, 'repl', e.target.value)}
                                       placeholder="Replace" style={{ flex: 1 }} />
                            {replacePairs.length > 1 &&
                                <Button icon="pi pi-times" text severity="danger" onClick={() => removeReplacePair(i)} />}
                        </div>
                    ))}
                    <Button label="+ Add pair" text onClick={addReplacePair} style={{ alignSelf: 'flex-start' }} />
                    <span style={{ color: '#888', fontSize: '0.8rem' }}>
                        {replaceField
                            ? `=replace(${replaceField}, ${replacePairs.flatMap(p => [`"${p.search}"`, `"${p.repl}"`]).join(', ')})`
                            : ''}
                    </span>
                </div>
            )}

            {(formulaType === 'addvat' || formulaType === 'substractvat') && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <label style={{ fontWeight: 500 }}>Source field</label>
                    <Dropdown value={vatField} options={colOptions} onChange={e => setVatField(e.value)}
                              placeholder="Select field" filter style={{ width: '100%' }} autoFocus />
                    <label style={{ fontWeight: 500 }}>Country</label>
                    <Dropdown value={vatCountry} options={VAT_COUNTRY_OPTIONS} onChange={e => setVatCountry(e.value)}
                              placeholder="Select country" filter style={{ width: '100%' }} />
                    {vatField && vatCountry &&
                        <span style={{ color: '#888', fontSize: '0.8rem' }}>
                            {`=${formulaType === 'addvat' ? 'addVat' : 'substractVat'}(${vatField}, ${vatPercent})`}
                        </span>}
                </div>
            )}
        </Dialog>
    );
}

const MappingTab = ({ mappingFields, currentMapping, handleMappingChange, dropdownOptions, preview }) => {
    const overlayRef  = useRef(null);
    const [activeField, setActiveField]     = useState(null);
    const [dialogType, setDialogType]       = useState(null);
    const [dialogVisible, setDialogVisible] = useState(false);

    const previewColumns = dropdownOptions
        .filter(o => o.value && !String(o.value).startsWith('='))
        .map(o => o.value);

    const openOverlay = (e, field) => {
        setActiveField(field);
        overlayRef.current.toggle(e);
    };

    const selectFormula = (type) => {
        overlayRef.current.hide();
        if (type === 'fakevin') { applyFormula('=fakeVin()'); return; }
        setDialogType(type);
        setDialogVisible(true);
    };

    const applyFormula = (formula) => {
        if (activeField) handleMappingChange(activeField, formula);
        setDialogVisible(false);
    };

    return (
        <div style={{ fontSize: '0.9rem' }}>
            {/* Shared overlay for formula type selection */}
            <OverlayPanel ref={overlayRef} style={{ minWidth: 260 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {FORMULA_OPTIONS.map(opt => (
                        <button key={opt.value}
                            onClick={() => selectFormula(opt.value)}
                            style={{
                                background: 'none', border: 'none', textAlign: 'left',
                                padding: '6px 10px', cursor: 'pointer', borderRadius: 4,
                                fontFamily: 'inherit', fontSize: '0.85rem',
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = '#f0f0f0'}
                            onMouseLeave={e => e.currentTarget.style.background = 'none'}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            </OverlayPanel>

            {/* Formula params dialog */}
            {dialogType && (
                <FormulaDialog
                    visible={dialogVisible}
                    formulaType={dialogType}
                    previewColumns={previewColumns}
                    onApply={applyFormula}
                    onHide={() => setDialogVisible(false)}
                />
            )}

            <div style={{ overflowX: 'auto', maxHeight: '61vh', overflowY: 'auto' }}>
                <table className="p-datatable-table" style={{ width: '100%', minWidth: 600 }}>
                    <thead>
                        <tr>
                            <th>Target Field</th>
                            <th>Source Column</th>
                            <th>Sample Value</th>
                        </tr>
                    </thead>
                    <tbody>
                        {mappingFields.map((field, idx) => {
                            const mappedCol = currentMapping[field];
                            const rowStyle = {
                                background: idx % 2 === 0 ? '#f9f9f9' : '#e9e9ef'
                            };

                            const dropdownDisabled = typeof mappedCol === 'string' && mappedCol.startsWith('=');

                            const firstNonEmpty = (arr) =>
                                Array.isArray(arr)
                                    ? String(arr.find(v => v !== '' && v !== null && v !== undefined) ?? '')
                                    : '';

                            // Resolve sample preview value, including formula columns like =year(Field1), =round(Field2)
                            const resolveSample = (col) => {
                                if (!col || !preview) return '';
                                if (typeof col === 'string' && col.startsWith('=')) {
                                    const yearMatch = col.match(/^=year\((.+)\)$/i);
                                    if (yearMatch) {
                                        const srcCol = yearMatch[1];
                                        const raw = firstNonEmpty(preview[srcCol]);
                                        if (raw) {
                                            const d = new Date(raw);
                                            const yr = isNaN(d.getFullYear()) ? null : d.getFullYear();
                                            return yr && yr > 2010 && yr < 2030 ? String(yr) : raw;
                                        }
                                    }
                                    if (/^=fakevin\(\)$/i.test(col)) {
                                        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
                                        return 'FAKE' + Array.from({ length: 13 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
                                    }
                                    const fakeVinMatch = col.match(/^=fakevinifempty\((.+)\)$/i);
                                    if (fakeVinMatch) {
                                        const raw = firstNonEmpty(preview[fakeVinMatch[1]]);
                                        if (raw) return raw;
                                        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
                                        return 'FAKE' + Array.from({ length: 13 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
                                    }
                                    const roundMatch = col.match(/^=round\((.+)\)$/i);
                                    if (roundMatch) {
                                        const srcCol = roundMatch[1];
                                        const raw = firstNonEmpty(preview[srcCol]);
                                        if (raw) {
                                            const num = parseFloat(raw);
                                            return isNaN(num) ? raw : String(Math.ceil(num / 1000) * 1000);
                                        }
                                    }
                                    const constMatch = col.match(/^=const\("(.*)"\)$/i);
                                    if (constMatch) return constMatch[1];
                                    const vatMatch = col.match(/^=(addvat|substractvat)\((.+),\s*([\d.]+)\)$/i);
                                    if (vatMatch) {
                                        const [, fn, srcCol, percentStr] = vatMatch;
                                        const raw = firstNonEmpty(preview[srcCol]);
                                        const num = parseFloat(raw);
                                        const percent = parseFloat(percentStr);
                                        if (raw && !isNaN(num)) {
                                            const result = fn.toLowerCase() === 'addvat'
                                                ? num * (1 + percent / 100)
                                                : num / (1 + percent / 100);
                                            return result.toFixed(2);
                                        }
                                        return raw;
                                    }
                                    const concatMatch = col.match(/^=concat\((.+)\)$/i);
                                    if (concatMatch) {
                                        const parts = concatMatch[1].match(/(?:[^,"]+|"[^"]*")+/g)?.map(s => s.trim()) ?? [];
                                        const sep = parts.length > 0 ? parts[0].replace(/^"|"$/g, '') : '';
                                        const fields = parts.slice(1);
                                        return fields.map(f => firstNonEmpty(preview[f])).join(sep);
                                    }
                                    const replaceMatch = col.match(/^=replace\((.+)\)$/i);
                                    if (replaceMatch) {
                                        const parts = replaceMatch[1].match(/(?:[^,"]+|"[^"]*")+/g)?.map(s => s.trim()) ?? [];
                                        const srcField = parts[0];
                                        let val = firstNonEmpty(preview[srcField]);
                                        for (let i = 1; i + 1 < parts.length; i += 2) {
                                            const search = parts[i].replace(/^"|"$/g, '');
                                            const repl   = parts[i + 1].replace(/^"|"$/g, '');
                                            val = val.split(search).join(repl);
                                        }
                                        return val;
                                    }
                                    return '';
                                }
                                return firstNonEmpty(preview[col]);
                            };

                            const sampleValue = resolveSample(mappedCol);
                            const isRowDisabled =
                                (field === 'Registration year' && !!currentMapping['Registration date']) ||
                                (field === 'UPE Net EUR (rounded)' && !!currentMapping['UPE Net EUR']);

                            return (
                                <tr key={field} style={rowStyle}>
                                    <td style={{ fontWeight: 500 }}>{field}</td>
                                    <td>
                                        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                            <button
                                                title="Insert formula"
                                                onClick={e => openOverlay(e, field)}
                                                disabled={isRowDisabled}
                                                style={{
                                                    flexShrink: 0,
                                                    width: 28, height: 28,
                                                    border: '1px solid #ced4da',
                                                    borderRadius: 4,
                                                    background: '#fff',
                                                    cursor: isRowDisabled ? 'default' : 'pointer',
                                                    opacity: isRowDisabled ? 0.4 : 1,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    fontStyle: 'italic', fontWeight: 700, fontSize: '0.85rem',
                                                    color: '#555', fontFamily: 'serif',
                                                }}
                                            >
                                                ƒ
                                            </button>
                                            <Dropdown
                                                value={currentMapping[field]}
                                                options={dropdownOptions}
                                                onChange={e => handleMappingChange(field, e.value)}
                                                style={{ flex: 1, ...(dropdownDisabled ? { background: '#d4edda' } : {}) }}
                                                placeholder="Select column"
                                                showClear
                                                filter
                                                filterPlaceholder="Search column..."
                                                disabled={isRowDisabled}
                                            />
                                        </div>
                                    </td>
                                    <td>
                                        <span
                                            style={{
                                                color: '#888',
                                                maxWidth: 300,
                                                display: 'inline-block',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                verticalAlign: 'middle'
                                            }}
                                            title={sampleValue}
                                        >
                                            {sampleValue}
                                        </span>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default MappingTab;
